"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  type Option,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import { RunsTestResults } from "@/components/tools/stats-tests-more/runs-test-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";
import {
  type CutoffRule,
  cutoffFor,
  dichotomize,
  runsTest,
} from "@/lib/tool-kit/stats-tests-more/runs-test";

/** English strings. A translated content file overrides these through `ui`. */
export const RUNS_TEST_CALCULATOR_STRINGS = {
  modeLabel: "Sequence type",
  numbersMode: "Numbers (split at a cutoff)",
  categoriesMode: "Two categories (letters or symbols)",
  numbersLabel:
    "Values in order — numbers separated by commas, spaces, or new lines",
  numbersPlaceholder: "12, 15, 9, 20, 17, 14",
  categoriesLabel:
    "Sequence in order — two different symbols, separated by spaces or commas, or run together (HHTHT)",
  categoriesPlaceholder: "H T T H H T H",
  valuesDetected: "one: item|other: items",
  cutoffLabel: "Cutoff for splitting the numbers",
  cutoffMedian: "Median",
  cutoffMean: "Mean",
  cutoffCustom: "Custom value",
  customLabel: "Custom cutoff",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided: the sequence is not random",
  less: "Too few runs: clustering or a trend",
  greater: "Too many runs: alternation",
  alphaLabel: "Significance level (α)",
  continuity: "Continuity correction (normal approximation)",
  tryExample: "Try an example",
  exampleNumbers:
    "12, 15, 9, 20, 17, 14, 11, 18, 13, 16, 21, 8, 10, 19, 22, 7, 23, 6, 24, 5",
  exampleCategories: "H H T T T T H H H T T T T",
  results: "Results",
  runs: "Runs (R)",
  n1: "Group 1 (n₁)",
  n2: "Group 2 (n₂)",
  expected: "Expected runs",
  z: "z",
  pValue: "p-value",
  methodExact:
    "Exact p-value: both groups have 20 or fewer values, where the exact distribution of R is the standard. The normal approximation gives {p}.",
  methodNormal:
    "Normal approximation: a group has more than 20 values, where the approximation is accurate. The exact p-value is {p}.",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the sequence does not look random.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence against randomness.",
  dropped:
    "one: {count} value equal to the cutoff was dropped before splitting.|other: {count} values equal to the cutoff were dropped before splitting.",
  cutoffIs: "Cutoff: {cutoff}. Above it (+) = {high}, below it (−) = {low}.",
  sequenceHeading: "The sequence and its runs",
  sequenceHint: "Each outlined block is one run of identical symbols.",
  sequenceCut: "Only the first 200 items are drawn.",
  plus: "+",
  minus: "−",
  allPHeading: "p-values for each method",
  alternative: "Alternative",
  pExact: "p (exact)",
  pNormal: "p (normal)",
  howCalculated: "How this was calculated",
  workingMean:
    "Expected runs: μ = 2 n₁ n₂ / N + 1 = 2 × {n1} × {n2} / {n} + 1 = {mu}",
  workingVariance:
    "Variance: σ² = 2 n₁ n₂ (2 n₁ n₂ − N) / (N² (N − 1)) = {variance}, so σ = {sigma}",
  workingZ: "z = (R − μ) / σ = ({r} − {mu}) / {sigma} = {z}",
  workingZCc: "With the continuity correction (0.5 toward μ): z = {z}",
  workingExact:
    "Exact: P(R ≤ {r}) = {less}; P(R ≥ {r}) = {greater}. The two-sided p-value is twice the smaller tail, capped at 1.",
  apaHeading: "APA-style result",
  apaExactTemplate: "*R* = {r}, *n*₁ = {n1}, *n*₂ = {n2}, exact *p* {p}",
  apaNormalTemplate: "*R* = {r}, *n*₁ = {n1}, *n*₂ = {n2}, *z* = {z}, *p* {p}",
  apaNote:
    "Name the test (Wald–Wolfowitz runs test) and, for numbers, the cutoff you used to split the sequence.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Enter a sequence of at least two values to test.",
  errorTwoSymbols:
    "The sequence has {count} different symbols; a runs test needs exactly two.",
  errorOneSymbol:
    "Every value falls on the same side of the cutoff (or is the same symbol), so there is only one run and nothing to test.",
} as const;

type Mode = "numbers" | "categories";
const SEPARATORS = /[\s,;]+/;

/** Tokens of a categorical sequence; a single unseparated word is split into characters. */
function categoryTokens(raw: string): string[] {
  const tokens = raw.split(SEPARATORS).filter((token) => token !== "");
  if (tokens.length === 1) {
    return [...tokens[0]];
  }
  return tokens;
}

export default function RunsTestCalculatorWidget() {
  const strings = useToolUi(RUNS_TEST_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("numbers");
  const [numbersText, setNumbersText] = useState("");
  const [categoriesText, setCategoriesText] = useState("");
  const [rule, setRule] = useState<CutoffRule>("median");
  const [customText, setCustomText] = useState("");
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [continuity, setContinuity] = useState(false);

  const numbers = parseNumberList(numbersText);
  const tokens = categoryTokens(categoriesText);
  const distinct = [...new Set(tokens)];
  const custom = parseNumberInput(customText);
  const cutoffReady =
    mode === "numbers" &&
    numbers.length > 0 &&
    (rule !== "custom" || custom !== null);
  const cutoff = cutoffReady ? cutoffFor(numbers, rule, custom ?? 0) : null;

  let symbols: number[] = [];
  let dropped = 0;
  let labels: [string, string] = [strings.plus, strings.minus];
  if (mode === "numbers" && cutoff !== null) {
    const split = dichotomize(numbers, cutoff);
    symbols = split.symbols;
    dropped = split.dropped;
  } else if (mode === "categories" && distinct.length <= 2) {
    symbols = tokens.map((token) => (token === distinct[0] ? 1 : 0));
    labels = [distinct[0], distinct[1] ?? ""];
  }
  const outcome =
    symbols.length >= 2 ? runsTest(symbols, { alternative, continuity }) : null;

  let notice: string = strings.prompt;
  if (mode === "categories" && distinct.length > 2) {
    notice = t(strings.errorTwoSymbols, { count: fmt(distinct.length) });
  } else if (outcome && !outcome.ok && outcome.reason === "one-symbol") {
    notice = strings.errorOneSymbol;
  }
  const count = (n: number) =>
    `${fmt(n)}${tPlural(strings.valuesDetected, locale, n)}`;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="rn-mode"
        onChange={setMode}
        options={[
          { value: "numbers", label: strings.numbersMode },
          { value: "categories", label: strings.categoriesMode },
        ]}
        value={mode}
      />
      {mode === "numbers" ? (
        <div className="space-y-4">
          <TextAreaField
            hint={count(numbers.length)}
            id="rn-numbers"
            label={strings.numbersLabel}
            onChange={setNumbersText}
            placeholder={strings.numbersPlaceholder}
            rows={4}
            value={numbersText}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="rn-cutoff"
              label={strings.cutoffLabel}
              onChange={setRule}
              options={[
                { value: "median", label: strings.cutoffMedian },
                { value: "mean", label: strings.cutoffMean },
                { value: "custom", label: strings.cutoffCustom },
              ]}
              value={rule}
            />
            {rule === "custom" ? (
              <TextField
                id="rn-custom"
                label={strings.customLabel}
                numeric
                onChange={setCustomText}
                value={customText}
              />
            ) : null}
          </div>
        </div>
      ) : (
        <TextAreaField
          hint={count(tokens.length)}
          id="rn-categories"
          label={strings.categoriesLabel}
          onChange={setCategoriesText}
          placeholder={strings.categoriesPlaceholder}
          rows={3}
          value={categoriesText}
        />
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="rn-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="rn-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
      </div>
      <Checkbox
        checked={continuity}
        label={strings.continuity}
        onChange={setContinuity}
      />
      <ExampleButton
        label={strings.tryExample}
        onClick={() => {
          setNumbersText(strings.exampleNumbers);
          setCategoriesText(strings.exampleCategories);
        }}
      />
      {outcome?.ok ? (
        <RunsTestResults
          alpha={Number(alpha)}
          continuity={continuity}
          cutoff={mode === "numbers" ? cutoff : null}
          dropped={dropped}
          fmt={fmt}
          labels={labels}
          locale={locale}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
